import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Grid,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { useSnackbar } from "components/GlobalSnackbar";
import CloseIcon from "@mui/icons-material/Close";
import { buttonStyles } from "utils/buttonStyles";
import { dialogHeaderStyles } from "utils/dialogStyles";

import { getUnpaidInboisByNoAkaun, apiGetInboisByNoInbois } from "api/inbois";
import { apiTerimaanExistsNoResit, apiCreateTerimaanBatch } from "api/terimaan";
import { apiLejarExistsNoResit } from "api/lejar";

function TambahTerimaan({ open, onClose, onSave, defaultNoAkaun }) {
  const [noInboisList, setNoInboisList] = useState([]);
  const [saving, setSaving] = useState(false); // prevent double submit that can cause 409

  // Fetch unpaid inbois when defaultNoAkaun changes
  useEffect(() => {
    if (!defaultNoAkaun) {
      setNoInboisList([]);
      setFormState((prev) => ({ ...prev, no_inbois: [], jumlah: "" }));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getUnpaidInboisByNoAkaun(defaultNoAkaun);
        if (!cancelled) setNoInboisList(
          (res.data || []).map((row) => ({
            value: row.no_inbois,
            label: `${row.no_inbois} (${row.tarikh_inbois ? new Date(row.tarikh_inbois).toLocaleDateString('en-CA') : ''})`,
            noAkaun: row.no_akaun,
            jumlah: row.jumlah,
          }))
        );
      } catch {
        if (!cancelled) setNoInboisList([]);
      }
    })();
    // Clear selection when account changes
    setFormState((prev) => ({ ...prev, no_inbois: [], jumlah: "" }));
    return () => { cancelled = true; };
  }, [defaultNoAkaun]);
  const [formState, setFormState] = useState({
    no_inbois: [],
    jumlah: "",
    tarikh_bayaran: "",
    no_resit: "",
    cara_bayaran: "",
    no_cek: "",
  });
  const [errors, setErrors] = useState({});
  const { showSuccess, showError } = useSnackbar();

  // Handle change for all fields (no async logic)
  const handleChange = (e) => {
    const { name, value } = e.target;
    // For multi-select no_inbois, value is an array
    if (name === 'no_inbois') {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      const next = arr.includes("") ? [] : arr;
      setFormState((prev) => ({ ...prev, [name]: next }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Autofill jumlah from selected inbois: if one selected, use list amount or fetch; if multiple, sum from list
  useEffect(() => {
    const calcJumlah = async () => {
      const selected = formState.no_inbois || [];
      if (!selected.length) {
        setFormState((prev) => ({ ...prev, jumlah: "" }));
        return;
      }
      const mapByNo = new Map((noInboisList || []).map(r => [r.value, r]));
      if (selected.length === 1) {
        const one = selected[0];
        const fromList = mapByNo.get(one)?.jumlah;
        const parsedList = parseFloat(fromList);
        if (Number.isFinite(parsedList)) {
          setFormState((prev) => ({ ...prev, jumlah: parsedList.toFixed(2) }));
          return;
        }
        try {
          const res = await apiGetInboisByNoInbois(one);
          const inbois = res?.data || {};
          const parsed = parseFloat(inbois?.jumlah);
          setFormState((prev) => ({ ...prev, jumlah: Number.isFinite(parsed) ? parsed.toFixed(2) : "" }));
        } catch (err) {
          console.error('[autofillJumlah] error fetching inbois:', err);
        }
        return;
      }
      // Multiple: sum from list; if any missing, fetch individually then sum
      try {
        const amounts = await Promise.all(selected.map(async (no) => {
          const fromList = parseFloat(mapByNo.get(no)?.jumlah);
          if (Number.isFinite(fromList)) return fromList;
          try {
            const res = await apiGetInboisByNoInbois(no);
            const parsed = parseFloat(res?.data?.jumlah);
            return Number.isFinite(parsed) ? parsed : 0;
          } catch (err) {
            console.error('[autofillJumlah] error fetching inbois (multi):', err);
            return 0;
          }
        }));
        const total = amounts.reduce((acc, n) => acc + (Number.isFinite(n) ? n : 0), 0);
        setFormState((prev) => ({ ...prev, jumlah: Number.isFinite(total) ? total.toFixed(2) : "" }));
      } catch (err) {
        console.error('[autofillJumlah] multi calc error:', err);
        setFormState((prev) => ({ ...prev, jumlah: "" }));
      }
    };
    calcJumlah();
  }, [formState.no_inbois, noInboisList]);

  const validateForm = () => {
    const newErrors = {};
  if (!formState.no_inbois || (Array.isArray(formState.no_inbois) && formState.no_inbois.length === 0)) newErrors.no_inbois = "No Inbois diperlukan";
    if (!formState.jumlah) newErrors.jumlah = "Jumlah diperlukan";
    if (!formState.tarikh_bayaran) newErrors.tarikh_bayaran = "Tarikh diperlukan";
    if (!formState.no_resit) newErrors.no_resit = "No Resit diperlukan";
    if (!formState.cara_bayaran) newErrors.cara_bayaran = "Cara Bayaran diperlukan";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (saving) return; // guard
    const selectedArr = Array.isArray(formState.no_inbois) ? formState.no_inbois : (formState.no_inbois ? [formState.no_inbois] : []);
    const multiSelected = selectedArr.length > 1;
    if (!validateForm()) return;
    // Helper: today in YYYY-MM-DD
    const today = new Date().toISOString().slice(0, 10);
    try {
      setSaving(true);
      // Guard duplicate no_resit across lejar and terimaan (one-time check for all cases)
      const noResit = (formState.no_resit || "").trim();
      if (!noResit) {
        showError("No Resit diperlukan");
        setSaving(false);
        return;
      }
      const [lejarExists, terimaanExists] = await Promise.all([
        apiLejarExistsNoResit(noResit),
        apiTerimaanExistsNoResit(noResit),
      ]);
      if (lejarExists?.exists || terimaanExists?.exists) {
        showError("No Resit telah wujud.");
        setSaving(false);
        return;
      }

      // Build payload with expected_jumlah from list when available
      const byNo = new Map((noInboisList || []).map(r => [r.value, r]));
      const items = selectedArr.map(no => {
        const amt = parseFloat(byNo.get(no)?.jumlah);
        return Number.isFinite(amt) ? { no_inbois: no, expected_jumlah: amt } : { no_inbois: no };
      });

      const payload = {
        no_akaun: defaultNoAkaun,
        no_resit: noResit,
        tarikh_bayaran: formState.tarikh_bayaran,
        cara_bayaran: formState.cara_bayaran,
        no_cek: formState.no_cek || null,
        items,
      };

      const resp = await apiCreateTerimaanBatch(payload);
      const count = resp?.count || items.length;
      const total = typeof resp?.total === 'number' ? resp.total : (parseFloat(formState.jumlah) || 0);
      showSuccess(`Berjaya simpan ${count} inbois (RM ${total.toFixed(2)}).`);
      setTimeout(async () => {
        try { await onSave({ ...formState }); } catch (_) {}
        handleClose();
      }, 1800);
    } catch (error) {
      console.error("[TambahTerimaan] Save flow error:", error);
      // Surface duplicate user-friendly message if 409
      const isDuplicate = error?.status === 409 || /wujud|duplicate/i.test(error?.message || '');
      showError(isDuplicate ? (error.message || 'Rekod terimaan telah wujud') : (error?.message || "Gagal menyimpan terimaan."));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormState({
      no_inbois: [],
      jumlah: "",
      tarikh_bayaran: "",
      no_resit: "",
      cara_bayaran: "",
      no_cek: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          },
        }}
      >
        <DialogTitle
          sx={{
            ...dialogHeaderStyles.report,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ color: "#fff" }}>
            Tambah Terimaan
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="error" fontWeight={700} fontSize="12px" sx={{ mb: 2 }}>
            Nota:<br />
            1. Jumlah terimaan mesti sama dengan jumlah inbois (bayaran penuh sahaja).<br />
            2. Untuk bayaran LEBIH, inbois perlu dijana dahulu oleh bahagian.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                size="small"
                required
                error={!!errors.no_inbois}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    fontSize: "0.875rem",
                    p: 1.2,
                    "&:hover fieldset": { borderColor: "#2196f3" },
                    "&.Mui-focused fieldset": { borderColor: "#2196f3" },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                }}
              >
                <InputLabel>No Inbois</InputLabel>
                <Select
                  multiple
                  label="No Inbois"
                  name="no_inbois"
                  value={formState.no_inbois}
                  onChange={handleChange}
                  MenuProps={{
                    MenuListProps: { dense: true, sx: { py: 0 } },
                    PaperProps: {
                      sx: {
                        '& .MuiMenuItem-root': { py: 0.25 },
                        '& .MuiCheckbox-root': { p: 0.25, mr: 1 },
                      },
                    },
                  }}
                  renderValue={(selected) => {
                    const arr = Array.isArray(selected) ? selected : (selected ? [selected] : []);
                    const labels = arr.map(v => (noInboisList || []).find(i => i.value === v)?.value || v);
                    if (labels.length <= 2) return labels.join(', ');
                    return `${labels.length} dipilih`;
                  }}
                >
                  <MenuItem value="">
                    <em>Kosongkan pilihan</em>
                  </MenuItem>
                  {(noInboisList || []).map((inbois) => {
                    const checked = (formState.no_inbois || []).includes(inbois.value);
                    return (
                      <MenuItem key={inbois.value} value={inbois.value} sx={{ py: 0.25 }}>
                        <Checkbox size="small" checked={checked} />
                        <ListItemText
                          primary={inbois.label}
                          primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.1 }}
                          secondary={`No Akaun: ${inbois.noAkaun}${Number.isFinite(parseFloat(inbois.jumlah)) ? ` • RM ${parseFloat(inbois.jumlah).toFixed(2)}` : ''}`}
                          secondaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.1 }}
                        />
                      </MenuItem>
                    );
                  })}
                  {(Array.isArray(formState.no_inbois) && formState.no_inbois.length > 0) && (
                    <MenuItem disabled sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper', py: 0.5 }}>
                      <ListItemText
                        primary={`Jumlah dipilih: RM ${(() => { const map = new Map((noInboisList||[]).map(r=>[r.value, r])); const sum = (formState.no_inbois||[]).reduce((acc, v)=> acc + (parseFloat(map.get(v)?.jumlah) || 0), 0); return sum.toFixed(2); })()}`}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 700 }}
                      />
                    </MenuItem>
                  )}
                </Select>
                {errors.no_inbois && (
                  <Typography variant="caption" color="error">{errors.no_inbois}</Typography>
                )}
                {!errors.no_inbois && Array.isArray(formState.no_inbois) && formState.no_inbois.length > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    {`Dipilih: ${formState.no_inbois.length} inbois. Jumlah akan disatukan.`}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Jumlah RM *"
                name="jumlah"
                value={formState.jumlah}
                error={!!errors.jumlah}
                helperText={errors.jumlah}
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                placeholder="0.00"
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tarikh Bayaran *"
                name="tarikh_bayaran"
                type="date"
                value={formState.tarikh_bayaran}
                onChange={handleChange}
                error={!!errors.tarikh_bayaran}
                helperText={errors.tarikh_bayaran}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="No. Resit (SAP) *"
                name="no_resit"
                value={formState.no_resit}
                onChange={handleChange}
                error={!!errors.no_resit}
                helperText={errors.no_resit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                size="small"
                required
                error={!!errors.cara_bayaran}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    fontSize: "0.875rem",
                    p: 1.2,
                    "&:hover fieldset": { borderColor: "#2196f3" },
                    "&.Mui-focused fieldset": { borderColor: "#2196f3" },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                }}
              >
                <InputLabel>Cara Bayaran</InputLabel>
                <Select
                  label="Cara Bayaran"
                  name="cara_bayaran"
                  value={formState.cara_bayaran}
                  onChange={handleChange}
                >
                  <MenuItem value=""><em>Pilih Cara Bayaran</em></MenuItem>
                  <MenuItem value="TUNAI">TUNAI</MenuItem>
                  <MenuItem value="CEK">CEK</MenuItem>
                  <MenuItem value="EFT">EFT</MenuItem>
                </Select>
                {errors.cara_bayaran && (
                  <Typography variant="caption" color="error">{errors.cara_bayaran}</Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="No. Cek"
                name="no_cek"
                value={formState.no_cek}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={saving}
            sx={{
              ...buttonStyles.save,
              m: 0,
              px: 2,
              minWidth: 0
            }}
          >
            {saving ? 'MENYIMPAN...' : 'SIMPAN'}
          </Button>
          <Button
            onClick={handleClose}
            variant="contained"
            size="small"
            sx={{
              ...buttonStyles.cancel,
              m: 0,
              px: 2,
              minWidth: 0
            }}
          >
            BATAL
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

TambahTerimaan.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  defaultNoAkaun: PropTypes.string,
};
TambahTerimaan.defaultProps = {
  onSave: () => {},
  defaultNoAkaun: "",
};

export default TambahTerimaan;
